import { ChevronLeft, ChevronRight, Download, Star } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAdminRatings, useExportRatingsCsv } from '@/hooks/use-admin'

const LIMIT = 100

export default function AdminRatingsPage() {
  const [page, setPage] = useState(1)
  const { data: resp, isLoading } = useAdminRatings({
    page: page.toString(),
    limit: LIMIT.toString(),
  })
  const exportCsv = useExportRatingsCsv()

  const ratings = resp?.data ?? []
  const pagination = resp?.pagination

  const handleExport = async () => {
    try {
      await exportCsv.mutateAsync({})
      toast.success('CSVをエクスポートしました')
    } catch {
      toast.error('エクスポートに失敗しました')
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">評価管理</h1>
        <Button onClick={handleExport} variant="outline" disabled={exportCsv.isPending}>
          <Download className="mr-2 h-4 w-4" />
          CSVエクスポート
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>評価一覧</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-48" />
          ) : ratings.length === 0 ? (
            <p className="text-muted-foreground">評価がありません。</p>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>レッスン</TableHead>
                    <TableHead>受講者</TableHead>
                    <TableHead>評価</TableHead>
                    <TableHead>コメント</TableHead>
                    <TableHead>日時</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ratings.map((item: any) => (
                    <TableRow key={item.feedback.id}>
                      <TableCell>{item.lessonTitle ?? '---'}</TableCell>
                      <TableCell>{item.userName ?? '---'}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                          <span>{item.feedback.rating}</span>
                        </div>
                      </TableCell>
                      <TableCell className="max-w-xs truncate">
                        {item.feedback.comment ?? '---'}
                      </TableCell>
                      <TableCell>
                        {new Date(item.feedback.createdAt).toLocaleDateString('ja-JP')}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {pagination && pagination.totalPages > 1 && (
                <div className="flex items-center justify-between pt-4">
                  <p className="text-sm text-muted-foreground">
                    {pagination.total} 件中 {(page - 1) * LIMIT + 1}〜
                    {Math.min(page * LIMIT, pagination.total)} 件表示
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => p - 1)}
                      disabled={page <= 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm">
                      {page} / {pagination.totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage((p) => p + 1)}
                      disabled={page >= pagination.totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
